import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useProfile } from '../useProfile'
import { ModelInfoCacheService } from '../../services/intellijetnet/model-info-cache.service'
import { supabase } from '../../lib/supabase'

export interface ABBEntry {
  abb_id?: string
  model_id: number
  year_model: number
  year: number
  quarter: number
  value: number | null
}

export interface MarketSettings {
  market?: string
  model_id: number
  production_start: number | null
  production_end: number | null
}

export interface RowData {
  year: number
  q1: number | null
  q2: number | null
  q3: number | null
  q4: number | null
}

export interface PendingChange {
  year: number
  quarter: number
  value: number | null
  oldValue: number | null
}

export interface ModelInfo {
  model_id: number
  make_name: string
  model_name: string
  display_name: string
}

export function useABBData() {
  const { modelId } = useParams<{ modelId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { profile } = useProfile()
  const [modelInfo, setModelInfo] = useState<ModelInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasAccess, setHasAccess] = useState(false)
  const [checkingAccess, setCheckingAccess] = useState(true)
  const [marketSettings, setMarketSettings] = useState<MarketSettings | null>(null)
  const [abbData, setAbbData] = useState<ABBEntry[]>([])
  const [rowData, setRowData] = useState<RowData[]>([])
  const [selectedYearModel, setSelectedYearModel] = useState<number | null>(null)
  const [yearModels, setYearModels] = useState<number[]>([])

  const fetchABBData = async (settings: MarketSettings) => {
    if (!settings.production_start) {
      setError('Production start year not configured')
      return
    }
    try {
      const { data, error } = await supabase
        .from('abb').select('*').eq('model_id', parseInt(modelId!, 10))
      if (error) throw error
      setAbbData(data || [])
      const endYear = settings.production_end || new Date().getFullYear()
      const models = Array.from(
        { length: endYear - settings.production_start! + 1 },
        (_, i) => settings.production_start! + i
      )
      setYearModels(models)
      if (!selectedYearModel && models.length > 0) {
        setSelectedYearModel(models[0])
      }
    } catch (err: any) {
      setError('Failed to load value data')
    }
  }

  const fetchModelData = async () => {
    try {
      setLoading(true)
      const numericModelId = parseInt(modelId!, 10)
      if (isNaN(numericModelId)) throw new Error('Invalid model ID')
      const info = await ModelInfoCacheService.getModelInfo(numericModelId)
      if (!info) throw new Error('Model not found')
      setModelInfo(info)
    } catch (err: any) {
      setError(err.message || 'Failed to load market data')
    } finally {
      setLoading(false)
    }
  }

  const fetchMarketSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('market_settings').select('*')
        .eq('model_id', parseInt(modelId!, 10)).maybeSingle()
      if (error && error.code !== 'PGRST116') throw error
      if (data) {
        setMarketSettings(data)
        fetchABBData(data)
      } else {
        setError('Market settings not found. Please configure production years first.')
      }
    } catch (err: any) {
      setError('Failed to load market settings')
    }
  }

  const checkAccess = async () => {
    try {
      setCheckingAccess(true)
      if (profile?.account_type === 'admin' || profile?.account_type === 'global_admin') {
        setHasAccess(true)
        fetchModelData()
        fetchMarketSettings()
        return
      }
      if (profile?.account_type === 'employee' && modelId) {
        const { data: marketAssignment } = await supabase
          .from('market_assignments').select('id')
          .eq('user_id', user!.id).eq('model_id', parseInt(modelId, 10)).single()
        if (marketAssignment) {
          setHasAccess(true)
          fetchModelData()
          fetchMarketSettings()
        } else {
          setHasAccess(false)
          setError('You do not have access to configure this market')
        }
      } else {
        setHasAccess(false)
        setError('You do not have permission to access this page')
      }
    } catch (err: any) {
      setError('Failed to verify access permissions')
    } finally {
      setCheckingAccess(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    if (modelId && user && profile) {
      checkAccess()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, user, profile])

  // Generate row data for the selected year model
  useEffect(() => {
    if (selectedYearModel && abbData && marketSettings) {
      const currentYear = new Date().getFullYear()
      const rows: RowData[] = []
      for (let year = selectedYearModel; year <= currentYear; year++) {
        const yearData = abbData.filter(
          d => d.year_model === selectedYearModel && d.year === year
        )
        rows.push({
          year,
          q1: yearData.find(d => d.quarter === 1)?.value || null,
          q2: yearData.find(d => d.quarter === 2)?.value || null,
          q3: yearData.find(d => d.quarter === 3)?.value || null,
          q4: yearData.find(d => d.quarter === 4)?.value || null
        })
      }
      setRowData(rows)
    }
  }, [selectedYearModel, abbData, marketSettings])

  return {
    modelId,
    navigate,
    modelInfo,
    loading,
    error,
    hasAccess,
    checkingAccess,
    marketSettings,
    abbData,
    rowData,
    setRowData,
    selectedYearModel,
    setSelectedYearModel,
    yearModels,
    fetchABBData,
  }
}
